import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosInstance } from 'axios';
import { APP_ROUTES } from '../constants';
import { APP_CONFIG } from '../utility/config';
import { convertMinuteToMiliseconds } from '../utility/helpers';
import { tokenManager } from '../utility/tokenManager';
import { TOKEN_TYPES } from '../constant/auth.constant';
import { getCurrentLang } from './langHeader';

const baseUrl = import.meta.env.VITE_BASE_URL || 'http://localhost:2375/v2';

type RefreshPromiseResult = { newAccessToken: string };
let refreshPromise: Promise<RefreshPromiseResult> | null = null;

/**
 * Main Authenticated API Instance
 *
 * Used for all authenticated endpoints after user login.
 * Automatically includes accessToken in Authorization header.
 * Handles token refresh on 401 responses.
 *
 * Token Management:
 * - Reads accessToken from tokenManager
 * - Automatically retries failed requests with refreshed token
 * - Stores new tokens via tokenManager on successful refresh
 */
const instance = axios.create({
  baseURL: baseUrl,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

const instanceMulitiPart = axios.create({
  baseURL: baseUrl,
  timeout: 3000,
  headers: { 'Content-Type': 'multipart/form-data' },
});

const adminInstance = axios.create({
  // backend path changed from '/admin' -> '/classveew'
  baseURL: `${baseUrl}/classveew`,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

const responseBody = (response: AxiosResponse) => response;

export const requests = {
  get: (url: string, params?: Record<string, any>): Promise<AxiosResponse> =>
    instance.get(url, { params }).then(responseBody),
  post: (url: string, body: any): Promise<AxiosResponse> =>
    instance.post(url, body).then(responseBody),
  postMultiPart: (url: string, body: any): Promise<AxiosResponse> =>
    instanceMulitiPart.post(url, body).then(responseBody),
  patch: (url: string, body: any): Promise<AxiosResponse> =>
    instance.patch(url, body).then(responseBody),
  put: (url: string, body: any): Promise<AxiosResponse> =>
    instance.put(url, body).then(responseBody),
  delete: (url: string): Promise<AxiosResponse> => instance.delete(url).then(responseBody),
};

/**
 * Request Interceptor: Add accessToken to all authenticated requests
 */
instance.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken(TOKEN_TYPES.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['Accept-Language'] = getCurrentLang();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

adminInstance.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken(TOKEN_TYPES.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['Accept-Language'] = getCurrentLang();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instanceMulitiPart.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken(TOKEN_TYPES.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['Accept-Language'] = getCurrentLang();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Response Interceptor: Handle token refresh on 401
 */
instance.interceptors.response.use(
  (response) => {
    if (response.data.code < 0) {
      return Promise.reject(response.data.result);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401) {
      if (originalRequest._retry) {
        const reason =
          (error.response?.data as { message: string })?.message ||
          'Your session has expired or is no longer valid. Please log in again.';
        window.location.replace(`${APP_ROUTES.UNAUTHORIZED}?reason=${encodeURIComponent(reason)}`);

        return Promise.reject(new Error(reason));
      }
      originalRequest._retry = true;
      return await handleUnauthorized(originalRequest);
    }

    return Promise.reject(error.response?.data || error.message);
  },
);

adminInstance.interceptors.response.use(
  (response) => {
    if (response.data.code < 0) {
      return Promise.reject(response.data.result);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401) {
      if (originalRequest._retry) {
        const reason =
          (error.response?.data as { message: string })?.message ||
          'Your session has expired or is no longer valid. Please log in again.';
        window.location.replace(`${APP_ROUTES.UNAUTHORIZED}?reason=${encodeURIComponent(reason)}`);

        return Promise.reject(new Error(reason));
      }
      originalRequest._retry = true;
      return await handleUnauthorized(originalRequest, adminInstance);
    }

    return Promise.reject(error.response?.data || error.message);
  },
);

instanceMulitiPart.interceptors.response.use(
  (response) => {
    if (response.data.code < 0) {
      return Promise.reject(response.data.result);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401) {
      if (originalRequest._retry) {
        const reason =
          (error.response?.data as { message: string })?.message ||
          'Your session has expired or is no longer valid. Please log in again.';
        window.location.replace(`${APP_ROUTES.UNAUTHORIZED}?reason=${encodeURIComponent(reason)}`);

        return Promise.reject(new Error(reason));
      }
      originalRequest._retry = true;
      return await handleUnauthorized(originalRequest);
    }

    return Promise.reject(error.response?.data || error.message);
  },
);

/**
 * Handle 401 response by attempting token refresh
 *
 * Strategy:
 * 1. Get refreshToken from tokenManager
 * 2. Call /auth/refresh endpoint
 * 3. Store new accessToken and refreshToken via tokenManager
 * 4. Retry original request with new accessToken
 *
 * CRITICAL: Uses shared refreshPromise to prevent multiple simultaneous refresh requests.
 * The promise is cleared IMMEDIATELY after token refresh completes (before retries),
 * allowing all queued requests to use the cached resolved promise value while enabling
 * new 401 errors to trigger fresh refreshes.
 */
async function handleUnauthorized(
  originalRequest: AxiosRequestConfig & { _retry?: boolean },
  retryInstance: AxiosInstance = instance,
): Promise<AxiosResponse> {
  // Create refresh promise if not already in progress
  if (!refreshPromise) {
    refreshPromise = (async (): Promise<RefreshPromiseResult> => {
      const refreshToken = tokenManager.getToken(TOKEN_TYPES.REFRESH_TOKEN);
      if (!refreshToken) {
        refreshPromise = null; // Clear before redirect
        tokenManager.clearAllTokens();
        window.location.replace(APP_ROUTES.UNAUTHORIZED);
        throw new Error('No refresh token available');
      }

      try {
        const response = await axios.post(
          `${baseUrl}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${refreshToken}` } },
        );

        const newAccessToken = response.headers['accesstoken'];
        const newRefreshToken = response.headers['refreshtoken'];

        // Store new tokens via tokenManager
        tokenManager.setTokens({
          [TOKEN_TYPES.ACCESS_TOKEN]: newAccessToken,
          [TOKEN_TYPES.REFRESH_TOKEN]: newRefreshToken,
        });

        console.debug('[api] Token refreshed successfully');
        
        // CRITICAL: Clear refreshPromise AFTER tokens are stored but BEFORE returning
        // This allows all queued requests to receive the resolved promise value
        // while enabling new 401s to trigger fresh refreshes
        refreshPromise = null;
        
        return { newAccessToken };
      } catch (error) {
        console.error('[api] Token refresh failed:', error);
        refreshPromise = null; // Clear on failure
        tokenManager.clearAllTokens();
        window.location.replace(APP_ROUTES.UNAUTHORIZED);
        throw error;
      }
    })();
  }

  // Store reference to current promise before awaiting
  // All concurrent 401s will share this same promise and get the same resolved value
  const currentRefreshPromise = refreshPromise;

  // Wait for the token refresh to complete - this BLOCKS until refresh finishes
  const { newAccessToken } = await currentRefreshPromise;

  // Update the original request with new token
  if (!originalRequest.headers) originalRequest.headers = {};
  originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

  // CRITICAL: Retry the original request and RETURN the awaited response
  // This ensures createAsyncThunk receives the actual retry response, not undefined
  return retryInstance(originalRequest);
}

/**
 * Set accessToken in axios headers (used after login)
 * @deprecated Use tokenManager.setToken('accessToken', token) instead
 */
export function setCoreApiAccessToken(token: string) {
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  adminInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  instanceMulitiPart.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export function setCoreApiXApiKey(token: string) {
  instance.defaults.headers.common['x-api-key'] = `${token}`;
  adminInstance.defaults.headers.common['x-api-key'] = `${token}`;
}

export function removeCoreApiAccessToken() {
  delete instance.defaults.headers.common['Authorization'];
  delete adminInstance.defaults.headers.common['Authorization'];
  delete instanceMulitiPart.defaults.headers.common['Authorization'];
}

export { adminInstance };
export default instance;
